import { defineStore } from 'pinia';
import { getData, getFileData, postData } from '@/services/axios-service';
import { BASE_URL } from '@/utils/constants/api-constants';
import {
  BillingState,
  Invoice,
  InvoiceDetail,
  PaymentLink,
} from '@/interfaces/billing/billing.interface';
import { PaginationResponse } from '@/interfaces/meta.interface';

const INITIAL_STATE: BillingState = {
  isLoading: false,
  pagination: {
    count: 0,
    limit: 10,
    page: 1,
    pages: 0,
  },
  invoicesInPage: [],
  invoiceSelected: null,
};

export const useBillingStore = defineStore({
  id: 'useBillingStore',
  state: (): BillingState => ({
    ...INITIAL_STATE,
  }),
  actions: {
    async getInvoices(page: number = 1) {
      try {
        if (page) this.pagination.page = page;

        const { data, success, error } = await getData<PaginationResponse<Invoice[]>>(
          `${BASE_URL.RENTALS}tenant/invoices`,
          {
            limit: this.pagination.limit,
            page: this.pagination.page,
          },
        );

        if (success && data) {
          const { rows, count, limit, pages, page } = data;

          this.pagination.count = count;
          this.pagination.limit = limit;
          this.pagination.page = page;
          this.pagination.pages = pages;

          return rows;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async getInvoice(invoiceId: string) {
      try {
        const { data, success, error } = await getData<InvoiceDetail>(
          `${BASE_URL.RENTALS}tenant/invoices/${invoiceId}`,
        );

        if (success && data) {
          this.invoiceSelected = data;
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async downloadInvoice(invoiceId: string) {
      try {
        return await getFileData<string>(`${BASE_URL.RENTALS}tenant/invoices/${invoiceId}/pdf`);
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async getPaymentLink(invoiceId: string) {
      try {
        const { data, success, error } = await postData<PaymentLink>(
          `${BASE_URL.RENTALS}tenant/invoices/${invoiceId}/send/link`,
        );

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
  },
});
